import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { VenueFormData, NewVenueFormData, DayHours, VenuePhoto, VenueDocument, VenueStatus, ChatThemeId, SpeakingStyleId, VenueRagUrl, TTSVoiceId, VenueSNSLink, SNSPlatform } from '@/types/venue';
import { VenueFormSchema, NewVenueFormSchema, getVenueStatusInfo, CHAT_THEMES, DEFAULT_CHAT_THEME, SPEAKING_STYLES, DEFAULT_SPEAKING_STYLE, getDefaultSpeakingStylePrompt, TTS_VOICES, DEFAULT_TTS_VOICE, COMMON_TIMEZONES, getBrowserTimezone, getAvailableTimezones, getTimezoneOffset, SNS_PLATFORMS, getSNSPlatformById } from '@/types/venue';
import { useI18n } from '@/hooks/useI18n';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useAuth } from '@/hooks/auth-context';
import { useVaultVenues } from '@/hooks/useVaultVenues';
import { useGeocode, buildAddressString } from '@/hooks/useGeocode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HoursEditor } from '@/components/HoursEditor';
import { PhotoUploader } from '@/components/PhotoUploader';
import { DocumentUploader } from '@/components/DocumentUploader';
import { Save, ArrowLeft, Loader2, AlertCircle, FileEdit, Eye, Archive, X, MapPin, CheckCircle2, Palette, MessageCircle, ChevronDown, ChevronUp, Link2, Plus, Trash2, Volume2, Play, VolumeX, Copy, Check, ExternalLink, User, LogOut, Globe, Share2, MessageSquare } from 'lucide-react';
import { useLineIntegration } from '@/hooks/useLineIntegration';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Status change confirmation dialog
interface StatusChangeDialogProps {
  isOpen: boolean;
  currentStatus: VenueStatus;
  newStatus: VenueStatus | null;
  venueName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const StatusChangeDialog = ({ isOpen, currentStatus, newStatus, venueName, onConfirm, onCancel, isLoading }: StatusChangeDialogProps) => {
  if (!isOpen || !newStatus) return null;

  const statusInfo = getVenueStatusInfo(newStatus);
  const currentStatusInfo = getVenueStatusInfo(currentStatus || 'draft');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Change Venue Status</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Are you sure you want to change the status of <strong>{venueName || 'this venue'}</strong> from{' '}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${currentStatusInfo.color}`}>
            {currentStatusInfo.label}
          </span>{' '}
          to{' '}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
            {statusInfo.label}
          </span>?
        </p>

        <p className="text-sm text-gray-500 mb-6">
          {statusInfo.description}
        </p>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Interface for tracking saved venue state (for dirty detection)
interface SavedVenueState {
  // Form fields
  title: string;
  description: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  };
  contact: {
    phone?: string;
    email?: string;
    website?: string;
  };
  vanitySlug: string;
  // Non-form state
  hours: DayHours[];
  photos: VenuePhoto[];
  documents: VenueDocument[];
  ragUrls: VenueRagUrl[];
  snsLinks: VenueSNSLink[];
  themeId: ChatThemeId;
  speakingStyleId: SpeakingStyleId;
  speakingStylePrompt: string;
  ttsVoiceId: TTSVoiceId;
  timezone: string;
}

// Simplified form for creating a new venue - just needs a name
interface NewVenueFormProps {
  onSubmit: (data: NewVenueFormData) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
  user?: { name?: string; email?: string } | null;
  onLogout?: () => void;
}

const NewVenueForm = ({ onSubmit, onCancel, isLoading, error, user, onLogout }: NewVenueFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewVenueFormData>({
    resolver: zodResolver(NewVenueFormSchema),
    defaultValues: {
      title: '',
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Left: Back */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-gray-300">|</span>
                <h1 className="text-sm font-medium text-gray-700">
                  Create New Venue
                </h1>
              </div>
            </div>

            {/* Right: User Menu */}
            {user && onLogout && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden md:inline max-w-[120px] truncate">
                      {user.name || user.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <button onClick={onCancel} className="w-full cursor-pointer">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Dashboard
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      <div className="container mx-auto px-4 max-w-lg py-8">
        <div className="bg-white rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Create New Venue</h1>
          <p className="text-gray-600 mb-6">
            Start by entering your restaurant name. You can add more details after creating the venue.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Failed to create venue</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-base">Restaurant Name *</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="e.g., Sakura Japanese Restaurant"
                className="mt-2 text-lg py-3"
                autoFocus
              />
              {errors.title && (
                <p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="flex-1">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Venue
                  </>
                )}
              </Button>
            </div>
          </form>

          <p className="text-sm text-gray-500 mt-6 text-center">
            After creating, you can add photos, hours, contact info, and more.
          </p>
        </div>
      </div>
    </div>
  );
};

// LINE Bot Integration Section Component
const LineIntegrationSection = ({ shortCode, venueId }: { shortCode: string; venueId: string }) => {
  const {
    verificationCode,
    lineStatus,
    isLoading,
    error,
    generateVerificationCode,
    getLineStatus,
    unlinkLine,
  } = useLineIntegration();
  const [isExpanded, setIsExpanded] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    if (isExpanded && !lineStatus) {
      getLineStatus(shortCode);
    }
  }, [isExpanded, shortCode, lineStatus, getLineStatus]);

  const handleGenerateCode = async () => {
    await generateVerificationCode(shortCode, venueId);
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleUnlink = async () => {
    if (confirm('Are you sure you want to unlink the LINE Bot from this venue?')) {
      await unlinkLine(shortCode);
      await getLineStatus(shortCode);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-green-600" />
          <h2 className="text-xl font-semibold">LINE Bot Integration</h2>
          {lineStatus?.linked && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Linked
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            Connect Yoyakun (LINE Bot) to this venue. Once linked, customers can interact with your AI concierge directly through LINE.
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Status Display */}
          {isLoading && !verificationCode ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading status...
            </div>
          ) : lineStatus?.linked ? (
            /* Linked State */
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">LINE Bot Connected</span>
                </div>
                <p className="text-sm text-green-700">
                  Active in {lineStatus.linkedGroupCount} group{lineStatus.linkedGroupCount !== 1 ? 's' : ''}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleUnlink}
                disabled={isLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                Unlink LINE Bot
              </Button>
            </div>
          ) : (
            /* Unlinked State */
            <div className="space-y-3">
              {verificationCode ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 mb-2">
                    Send this code in a LINE chat with Yoyakun to link your venue:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-2xl font-mono font-bold tracking-wider text-blue-900 bg-white px-4 py-2 rounded border">
                      {verificationCode.code}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyCode(verificationCode.code)}
                    >
                      {codeCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Expires in {verificationCode.expiresInMinutes} minutes
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-600 mb-3">
                    Not linked. Generate a verification code to connect Yoyakun to this venue.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleGenerateCode}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Verification Code'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const VenueEditPage = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { language } = useI18n();
  const navigate = useNavigate();
  const isNewVenue = venueId === 'new';

  useDocumentLang();

  // Auth check
  const { isAuthenticated, isLoading: authLoading, session, user, logout } = useAuth();

  // Venue management
  const {
    currentVenue,
    loading: venueLoading,
    error: venueError,
    getVenue,
    createVenue,
    updateVenue,
    uploadFile,
    clearError,
  } = useVaultVenues();

  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState<DayHours[]>([]);
  const [photos, setPhotos] = useState<VenuePhoto[]>([]);
  const [documents, setDocuments] = useState<VenueDocument[]>([]);
  const [ragUrls, setRagUrls] = useState<VenueRagUrl[]>([]);
  const [snsLinks, setSnsLinks] = useState<VenueSNSLink[]>([]);
  const [editingSnsLinkId, setEditingSnsLinkId] = useState<string | null>(null); // Track which SNS link is being edited
  const [saveError, setSaveError] = useState<string | null>(null);
  const [currentTheme, setCurrentTheme] = useState<ChatThemeId>(DEFAULT_CHAT_THEME);

  // Speaking style
  const [currentSpeakingStyleId, setCurrentSpeakingStyleId] = useState<SpeakingStyleId>(DEFAULT_SPEAKING_STYLE);
  const [currentSpeakingStylePrompt, setCurrentSpeakingStylePrompt] = useState<string>(getDefaultSpeakingStylePrompt());
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [showAdvancedPrompt, setShowAdvancedPrompt] = useState(false);

  // TTS Voice
  const [currentTTSVoice, setCurrentTTSVoice] = useState<TTSVoiceId>(DEFAULT_TTS_VOICE);
  const [ttsPreviewText, setTtsPreviewText] = useState<string>('こんにちは、ご予約についてお手伝いいたします。');
  const [ttsPreviewPlaying, setTtsPreviewPlaying] = useState(false);

  // Timezone
  const [currentTimezone, setCurrentTimezone] = useState<string>(getBrowserTimezone());
  const [showAllTimezones, setShowAllTimezones] = useState(false);

  // Geocoding
  const { geocode, isLoading: geocodeLoading, error: geocodeError, clearError: clearGeocodeError } = useGeocode();
  const [geocodeSuccess, setGeocodeSuccess] = useState(false);

  // RAG URL management
  const [ragUrlInputs, setRagUrlInputs] = useState<string[]>(['']); // Array of URL input values
  const [ragUrlValidating, setRagUrlValidating] = useState<number | null>(null); // Index being validated
  const [ragUrlError, setRagUrlError] = useState<string | null>(null);
  const [ragUrlErrorIndex, setRagUrlErrorIndex] = useState<number | null>(null); // Which input has the error
  const [ragUrlDisclaimer, setRagUrlDisclaimer] = useState(false); // Disclaimer accepted

  // Status management
  const [currentStatus, setCurrentStatus] = useState<VenueStatus>('draft');
  const [statusDialog, setStatusDialog] = useState<{
    isOpen: boolean;
    newStatus: VenueStatus | null;
  }>({ isOpen: false, newStatus: null });
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // URL copy feedback
  const [urlCopied, setUrlCopied] = useState(false);

  // Debug: All vault assets
  const [debugAssets, setDebugAssets] = useState<unknown[]>([]);
  const [debugAssetsLoading, setDebugAssetsLoading] = useState(false);
  const [debugAssetsError, setDebugAssetsError] = useState<string | null>(null);
  const [showDebugAssets, setShowDebugAssets] = useState(false);

  // Saved state for dirty detection (null until venue is loaded)
  const [savedState, setSavedState] = useState<SavedVenueState | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty: formIsDirty },
    reset,
    watch,
    setValue,
  } = useForm<VenueFormData>({
    resolver: zodResolver(VenueFormSchema),
    defaultValues: {
      title: '',
      description: '',
      address: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        latitude: undefined,
        longitude: undefined,
      },
      contact: {
        phone: '',
        email: '',
        website: '',
      },
      hours: [],
      vanitySlug: '',
      status: 'draft',
    },
  });

  // Calculate if there are unsaved changes
  const isDirty = useMemo(() => {
    // For new venues, consider dirty if form has any content
    if (isNewVenue) return false; // New venue form has its own submission

    // If no saved state yet (still loading), not dirty
    if (!savedState) return false;

    // Check form fields (react-hook-form tracks this)
    if (formIsDirty) return true;

    // Check non-form state: hours
    if (JSON.stringify(hours) !== JSON.stringify(savedState.hours)) return true;

    // Check photos
    if (JSON.stringify(photos) !== JSON.stringify(savedState.photos)) return true;

    // Check documents
    if (JSON.stringify(documents) !== JSON.stringify(savedState.documents)) return true;

    // Check RAG URLs
    if (JSON.stringify(ragUrls) !== JSON.stringify(savedState.ragUrls)) return true;

    // Check SNS links
    if (JSON.stringify(snsLinks) !== JSON.stringify(savedState.snsLinks)) return true;

    // Check theme
    if (currentTheme !== savedState.themeId) return true;

    // Check speaking style
    if (currentSpeakingStyleId !== savedState.speakingStyleId) return true;
    if (currentSpeakingStylePrompt !== savedState.speakingStylePrompt) return true;

    // Check TTS voice
    if (currentTTSVoice !== savedState.ttsVoiceId) return true;

    // Check timezone
    if (currentTimezone !== savedState.timezone) return true;

    return false;
  }, [
    isNewVenue,
    savedState,
    formIsDirty,
    hours,
    photos,
    documents,
    ragUrls,
    snsLinks,
    currentTheme,
    currentSpeakingStyleId,
    currentSpeakingStylePrompt,
    currentTTSVoice,
    currentTimezone,
  ]);

  // Warn user before leaving page with unsaved changes (browser navigation, refresh, close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but we still need to set returnValue
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate(`/${language}/login`);
    }
  }, [authLoading, isAuthenticated, navigate, language]);

  // Load venue data if editing existing venue
  useEffect(() => {
    if (!isNewVenue && venueId && isAuthenticated) {
      loadVenue(venueId);
    }
  }, [venueId, isNewVenue, isAuthenticated]);

  const loadVenue = async (id: string) => {
    try {
      clearError();
      const venue = await getVenue(id);
      reset({
        title: venue.title,
        description: venue.description,
        address: venue.address,
        contact: venue.contact,
        vanitySlug: venue.vanitySlug,
        hours: venue.hours,
        status: venue.status,
      });
      setHours(venue.hours || []);
      setPhotos(venue.photos || []);
      setDocuments(venue.documents || []);
      setRagUrls(venue.ragUrls || []);
      setSnsLinks(venue.snsLinks || []);
      setCurrentStatus((venue.status as VenueStatus) || 'draft');
      setCurrentTheme(venue.themeId || DEFAULT_CHAT_THEME);

      // Load speaking style
      if (venue.speakingStylePrompt) {
        setCurrentSpeakingStylePrompt(venue.speakingStylePrompt);
        // Check if prompt matches a preset
        const matchingStyle = SPEAKING_STYLES.find(s => s.prompt === venue.speakingStylePrompt);
        if (matchingStyle) {
          setCurrentSpeakingStyleId(matchingStyle.id);
          setIsCustomPrompt(false);
        } else {
          // Custom prompt that doesn't match any preset
          setCurrentSpeakingStyleId(venue.speakingStyleId || DEFAULT_SPEAKING_STYLE);
          setIsCustomPrompt(true);
          setShowAdvancedPrompt(true); // Show the advanced section so they can see their custom prompt
        }
      } else if (venue.speakingStyleId) {
        setCurrentSpeakingStyleId(venue.speakingStyleId);
        const style = SPEAKING_STYLES.find(s => s.id === venue.speakingStyleId);
        setCurrentSpeakingStylePrompt(style?.prompt || getDefaultSpeakingStylePrompt());
        setIsCustomPrompt(false);
      }

      // Load TTS voice
      if (venue.ttsVoiceId) {
        setCurrentTTSVoice(venue.ttsVoiceId);
      }

      // Load timezone (default to browser timezone if not set)
      const loadedTimezone = venue.timezone || getBrowserTimezone();
      if (venue.timezone) {
        setCurrentTimezone(venue.timezone);
      } else {
        // For existing venues without timezone, suggest browser timezone
        setCurrentTimezone(loadedTimezone);
      }

      // Determine the speaking style values for saved state
      let loadedSpeakingStyleId = DEFAULT_SPEAKING_STYLE;
      let loadedSpeakingStylePrompt = getDefaultSpeakingStylePrompt();
      if (venue.speakingStylePrompt) {
        loadedSpeakingStylePrompt = venue.speakingStylePrompt;
        const matchingStyle = SPEAKING_STYLES.find(s => s.prompt === venue.speakingStylePrompt);
        loadedSpeakingStyleId = matchingStyle?.id || venue.speakingStyleId || DEFAULT_SPEAKING_STYLE;
      } else if (venue.speakingStyleId) {
        loadedSpeakingStyleId = venue.speakingStyleId;
        const style = SPEAKING_STYLES.find(s => s.id === venue.speakingStyleId);
        loadedSpeakingStylePrompt = style?.prompt || getDefaultSpeakingStylePrompt();
      }

      // Save the initial state for dirty detection
      setSavedState({
        title: venue.title,
        description: venue.description,
        address: venue.address,
        contact: venue.contact,
        vanitySlug: venue.vanitySlug,
        hours: venue.hours || [],
        photos: venue.photos || [],
        documents: venue.documents || [],
        ragUrls: venue.ragUrls || [],
        snsLinks: venue.snsLinks || [],
        themeId: venue.themeId || DEFAULT_CHAT_THEME,
        speakingStyleId: loadedSpeakingStyleId,
        speakingStylePrompt: loadedSpeakingStylePrompt,
        ttsVoiceId: venue.ttsVoiceId || DEFAULT_TTS_VOICE,
        timezone: loadedTimezone,
      });
    } catch (error) {
      console.error('Failed to load venue:', error);
    }
  };

  // Debug: Fetch all vault assets via backend proxy
  const fetchDebugAssets = async () => {
    if (!session?.token) return;

    setDebugAssetsLoading(true);
    setDebugAssetsError(null);

    try {
      const response = await fetch('/api/vault/debug/assets', {
        headers: {
          'Authorization': `Bearer ${session.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || `Failed to fetch assets: ${response.status}`);
      }

      const data = await response.json();
      setDebugAssets(data.data || []);
      console.log('[Debug] All vault assets:', data);
    } catch (error) {
      console.error('[Debug] Failed to fetch assets:', error);
      setDebugAssetsError(error instanceof Error ? error.message : 'Failed to fetch assets');
    } finally {
      setDebugAssetsLoading(false);
    }
  };

  // Status change handlers
  const handleStatusChangeRequest = (newStatus: VenueStatus) => {
    if (newStatus !== currentStatus) {
      setStatusDialog({ isOpen: true, newStatus });
    }
  };

  const handleStatusChangeConfirm = async () => {
    if (!statusDialog.newStatus || !venueId || isNewVenue) return;

    try {
      setStatusChangeLoading(true);
      await updateVenue(venueId, { status: statusDialog.newStatus });
      setCurrentStatus(statusDialog.newStatus);
      setStatusDialog({ isOpen: false, newStatus: null });
    } catch (error) {
      console.error('Failed to update venue status:', error);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleStatusChangeCancel = () => {
    setStatusDialog({ isOpen: false, newStatus: null });
  };

  // Get the chat URL for the venue
  const getChatUrl = () => {
    const shortCode = currentVenue?.shortCode;
    const slug = currentVenue?.vanitySlug || watch('vanitySlug') || 'chat';
    if (shortCode) {
      return `${window.location.origin}/${language}/talk/${shortCode}/${slug}`;
    }
    return null;
  };

  // Copy chat URL to clipboard
  const handleCopyUrl = async () => {
    const url = getChatUrl();
    if (url) {
      try {
        await navigator.clipboard.writeText(url);
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy URL:', error);
      }
    }
  };

  // Open chat URL in new tab
  const handleOpenChat = () => {
    const url = getChatUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Theme change handler (no auto-save, waits for Save button)
  const handleThemeChange = (themeId: ChatThemeId) => {
    setCurrentTheme(themeId);
  };

  // Speaking style change handler (no auto-save, waits for Save button)
  const handleSpeakingStyleChange = (styleId: SpeakingStyleId) => {
    const style = SPEAKING_STYLES.find(s => s.id === styleId);
    if (!style) return;

    setCurrentSpeakingStyleId(styleId);
    setCurrentSpeakingStylePrompt(style.prompt);
    setIsCustomPrompt(false);
  };

  // Custom prompt change handler
  const handleCustomPromptChange = (newPrompt: string) => {
    setCurrentSpeakingStylePrompt(newPrompt);
    // Check if it matches a preset
    const matchingStyle = SPEAKING_STYLES.find(s => s.prompt === newPrompt);
    if (matchingStyle) {
      setIsCustomPrompt(false);
      setCurrentSpeakingStyleId(matchingStyle.id);
    } else {
      setIsCustomPrompt(true);
    }
  };

  // Reset to preset prompt
  const handleResetToPreset = () => {
    const style = SPEAKING_STYLES.find(s => s.id === currentSpeakingStyleId);
    if (style) {
      setCurrentSpeakingStylePrompt(style.prompt);
      setIsCustomPrompt(false);
    }
  };

  // TTS voice change handler (no auto-save, waits for Save button)
  const handleTTSVoiceChange = (voiceId: TTSVoiceId) => {
    setCurrentTTSVoice(voiceId);
  };

  // Timezone change handler (no auto-save, waits for Save button)
  const handleTimezoneChange = (timezone: string) => {
    setCurrentTimezone(timezone);
  };

  // TTS preview handler (placeholder - to be implemented with actual TTS API)
  const handleTTSPreview = async () => {
    if (currentTTSVoice === 'voice-none') return;

    setTtsPreviewPlaying(true);
    // TODO: Implement actual TTS preview API call
    // For now, just simulate a preview duration
    console.log(`[TTS Preview] Playing voice: ${currentTTSVoice}, text: ${ttsPreviewText}`);

    setTimeout(() => {
      setTtsPreviewPlaying(false);
    }, 2000);
  };

  // RAG URL handlers
  const handleRagUrlInputChange = (index: number, value: string) => {
    const newInputs = [...ragUrlInputs];
    newInputs[index] = value;
    setRagUrlInputs(newInputs);
    // Clear error if user is editing the errored input
    if (ragUrlErrorIndex === index) {
      setRagUrlError(null);
      setRagUrlErrorIndex(null);
    }
  };

  const handleAddRagUrlInput = () => {
    setRagUrlInputs([...ragUrlInputs, '']);
  };

  const handleRemoveRagUrlInput = (index: number) => {
    const newInputs = ragUrlInputs.filter((_, i) => i !== index);
    setRagUrlInputs(newInputs.length > 0 ? newInputs : ['']);
    // Clear error if removing the errored input
    if (ragUrlErrorIndex === index) {
      setRagUrlError(null);
      setRagUrlErrorIndex(null);
    } else if (ragUrlErrorIndex !== null && ragUrlErrorIndex > index) {
      // Adjust error index if we removed an input before the errored one
      setRagUrlErrorIndex(ragUrlErrorIndex - 1);
    }
  };

  const handleValidateAndAddUrl = async (index: number) => {
    const url = ragUrlInputs[index].trim();
    if (!url) {
      setRagUrlError('Please enter a URL');
      setRagUrlErrorIndex(index);
      return;
    }

    if (!ragUrlDisclaimer) {
      setRagUrlError('Please confirm you have the right to scrape this URL');
      setRagUrlErrorIndex(null); // Not a specific input error
      return;
    }

    // Check if URL already exists
    if (ragUrls.some(r => r.url === url)) {
      setRagUrlError('This URL has already been added');
      setRagUrlErrorIndex(index);
      return;
    }

    setRagUrlValidating(index);
    setRagUrlError(null);
    setRagUrlErrorIndex(null);

    try {
      const response = await fetch('/api/url-validator/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      const result = await response.json();

      if (!result.valid) {
        setRagUrlError(result.error || 'URL validation failed');
        setRagUrlErrorIndex(index);
        setRagUrlValidating(null);
        return;
      }

      // Add the validated URL
      const newRagUrl: VenueRagUrl = {
        id: crypto.randomUUID(),
        url: result.url || url,
        title: result.title,
        addedAt: new Date().toISOString(),
        status: 'pending',
      };

      const updatedRagUrls = [...ragUrls, newRagUrl];
      setRagUrls(updatedRagUrls);

      // Clear the input
      const newInputs = [...ragUrlInputs];
      newInputs[index] = '';
      setRagUrlInputs(newInputs);
      // No auto-save - waits for Save button
    } catch (error) {
      console.error('URL validation error:', error);
      setRagUrlError('Failed to validate URL. Please try again.');
      setRagUrlErrorIndex(index);
    } finally {
      setRagUrlValidating(null);
    }
  };

  // Remove RAG URL (no auto-save, waits for Save button)
  const handleRemoveRagUrl = (urlId: string) => {
    const updatedRagUrls = ragUrls.filter(r => r.id !== urlId);
    setRagUrls(updatedRagUrls);
  };

  // Geocoding handler
  const handleGeocode = async () => {
    clearGeocodeError();
    setGeocodeSuccess(false);

    const addressData = watch('address');
    const fullAddress = buildAddressString(addressData);

    if (!fullAddress.trim()) {
      return;
    }

    const result = await geocode(fullAddress);
    if (result) {
      // Update form values with the geocoded coordinates using setValue
      // This keeps the form dirty and ensures values are submitted
      setValue('address.latitude', result.latitude, { shouldDirty: true });
      setValue('address.longitude', result.longitude, { shouldDirty: true });

      console.log('[VenueEditPage] Geocode result set:', {
        latitude: result.latitude,
        longitude: result.longitude,
      });

      setGeocodeSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setGeocodeSuccess(false), 3000);
    }
  };

  // Simplified handler for new venue creation - just needs the name
  const onNewVenueSubmit = async (data: NewVenueFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      console.log('[VenueEditPage] Creating new venue with title:', data.title);

      const venue = await createVenue({ title: data.title });
      // Navigate to the edit page for the new venue
      navigate(`/${language}/edit/${venue.id}`, { replace: true });
    } catch (error) {
      console.error('Failed to create venue:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to create venue');
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (data: VenueFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      console.log('[VenueEditPage] Form data on submit:', {
        address: data.address,
        latitude: data.address?.latitude,
        longitude: data.address?.longitude,
      });

      const payload = {
        ...data,
        hours,
        photos,
        documents,
        ragUrls,
        snsLinks,
        themeId: currentTheme,
        speakingStyleId: currentSpeakingStyleId,
        speakingStylePrompt: currentSpeakingStylePrompt,
        ttsVoiceId: currentTTSVoice,
        timezone: currentTimezone,
      };

      console.log('[VenueEditPage] Payload being sent:', payload);

      // For existing venues, just update
      if (venueId) {
        await updateVenue(venueId, payload);

        // Update saved state to reflect the new saved values
        setSavedState({
          title: data.title,
          description: data.description,
          address: data.address,
          contact: data.contact,
          vanitySlug: data.vanitySlug,
          hours,
          photos,
          documents,
          ragUrls,
          snsLinks,
          themeId: currentTheme,
          speakingStyleId: currentSpeakingStyleId,
          speakingStylePrompt: currentSpeakingStylePrompt,
          ttsVoiceId: currentTTSVoice,
          timezone: currentTimezone,
        });

        // Reset form dirty state
        reset(data, { keepValues: true });
      }
    } catch (error) {
      console.error('Failed to save venue:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save venue');
    } finally {
      setSaving(false);
    }
  };

  // File upload handlers using vault backend
  const handlePhotoUpload = async (file: File): Promise<VenuePhoto> => {
    try {
      const result = await uploadFile(file, 'photo', venueId !== 'new' ? venueId : undefined);
      return {
        id: result.id,
        url: result.url,
        filename: result.filename,
        order: photos.length,
        uploadedAt: result.uploadedAt,
      };
    } catch (error) {
      console.error('Photo upload failed:', error);
      throw error;
    }
  };

  const handleDocumentUpload = async (file: File): Promise<VenueDocument> => {
    try {
      const result = await uploadFile(file, 'document', venueId !== 'new' ? venueId : undefined);
      let fileType: 'pdf' | 'txt' | 'text' | 'md' | 'csv';
      if (file.type === 'application/pdf') {
        fileType = 'pdf';
      } else if (file.type === 'text/markdown' || file.name.endsWith('.md')) {
        fileType = 'md';
      } else if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        fileType = 'csv';
      } else {
        fileType = 'txt';
      }
      return {
        id: result.id,
        url: result.url,
        filename: result.filename,
        fileType,
        uploadedAt: result.uploadedAt,
        processed: false, // Will be updated when RAG processing is implemented
      };
    } catch (error) {
      console.error('Document upload failed:', error);
      throw error;
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading while fetching venue
  if (venueLoading && !isNewVenue) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading venue...</p>
        </div>
      </div>
    );
  }

  // Show error if venue failed to load
  if (venueError && !isNewVenue) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load venue</h2>
          <p className="text-gray-600 mb-4">{venueError}</p>
          <Button onClick={() => navigate(`/${language}/admin`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Show simplified form for new venues - just the restaurant name
  if (isNewVenue) {
    return (
      <NewVenueForm
        onSubmit={onNewVenueSubmit}
        onCancel={() => navigate(`/${language}/admin`)}
        isLoading={saving}
        error={saveError}
        user={user}
        onLogout={logout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/${language}/admin`)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-gray-300">|</span>
                <h1 className="text-sm font-medium text-gray-700 truncate max-w-[250px]">
                  {watch('title') || 'Edit Venue'}
                </h1>
                {/* Status Badge */}
                {!isNewVenue && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getVenueStatusInfo(currentStatus).color}`}>
                    {getVenueStatusInfo(currentStatus).label}
                  </span>
                )}
              </div>
            </div>

            {/* Right: Save + User Menu */}
            <div className="flex items-center gap-3">
              {/* Save Button */}
              <Button
                type="submit"
                form="venue-edit-form"
                disabled={saving || ragUrlErrorIndex !== null || !isDirty}
                size="sm"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Save Changes</span>
                  </>
                )}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden md:inline max-w-[120px] truncate">
                      {user?.name || user?.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <button
                      onClick={() => navigate(`/${language}/admin`)}
                      className="w-full cursor-pointer"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Dashboard
                    </button>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-14" />

      <div className="container mx-auto px-4 max-w-4xl py-8">
        {/* Status & Chat URL Section - Only show for existing venues */}
        {!isNewVenue && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              {/* Publication Status */}
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-3">Publication Status</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {getVenueStatusInfo(currentStatus).description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(['draft', 'published', 'archived'] as VenueStatus[]).map((status) => {
                    const info = getVenueStatusInfo(status);
                    const isActive = status === currentStatus;
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => handleStatusChangeRequest(status)}
                        className={`
                          flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all text-sm
                          ${isActive
                            ? `${info.color} border-current font-medium`
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                          }
                        `}
                      >
                        {status === 'draft' && <FileEdit className="h-4 w-4" />}
                        {status === 'published' && <Eye className="h-4 w-4" />}
                        {status === 'archived' && <Archive className="h-4 w-4" />}
                        {info.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Chat URL */}
              {getChatUrl() && (
                <div className="lg:w-80 lg:border-l lg:pl-6">
                  <h2 className="text-lg font-semibold mb-3">Chat URL</h2>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                    <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <code className="text-sm text-gray-700 truncate flex-1 font-mono">
                      {getChatUrl()?.replace(window.location.origin, '')}
                    </code>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyUrl}
                      className="flex-1"
                    >
                      {urlCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy URL
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleOpenChat}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat Theme Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="h-5 w-5 text-gray-600" />
            <h2 className="text-xl font-semibold">Chat Theme</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Choose a visual theme for your chat interface. This affects colors and styling visitors see.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {CHAT_THEMES.map((theme) => {
              const isActive = theme.id === currentTheme;
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => handleThemeChange(theme.id)}
                  className={`
                    flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                    ${isActive
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                    }
                  `}
                >
                  <span className={`w-8 h-8 rounded-full ${theme.color} shadow-inner`}></span>
                  <span className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                    {theme.name}
                  </span>
                  {isActive && (
                    <span className="text-xs text-blue-600">Selected</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Speaking Style Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="h-5 w-5 text-gray-600" />
            <h2 className="text-xl font-semibold">Speaking Style</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Choose how your AI concierge communicates with guests. This affects the tone and personality of conversations.
          </p>

          {/* Style Presets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            {SPEAKING_STYLES.map((style) => {
              const isActive = style.id === currentSpeakingStyleId && !isCustomPrompt;
              return (
                <button
                  key={style.id}
                  type="button"
                  onClick={() => handleSpeakingStyleChange(style.id)}
                  className={`
                    flex flex-col items-start gap-1 p-4 rounded-lg border-2 transition-all text-left
                    ${isActive
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                    }
                  `}
                >
                  <span className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                    {style.name}
                  </span>
                  <span className="text-xs text-gray-500 line-clamp-2">
                    {style.description}
                  </span>
                  {isActive && (
                    <span className="text-xs text-blue-600 mt-1">Selected</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom indicator */}
          {isCustomPrompt && (
            <div className="mb-4 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Using custom speaking style based on "{SPEAKING_STYLES.find(s => s.id === currentSpeakingStyleId)?.name}" preset
              </p>
            </div>
          )}

          {/* Advanced: Custom Prompt Section */}
          <div className="border-t pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowAdvancedPrompt(!showAdvancedPrompt)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {showAdvancedPrompt ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span>Advanced: Customize Prompt</span>
            </button>

            {showAdvancedPrompt && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">
                  Customize the speaking style instructions. Changes here create a custom style that won't be affected by future preset updates.
                </p>
                <textarea
                  value={currentSpeakingStylePrompt}
                  onChange={(e) => handleCustomPromptChange(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                  placeholder="Enter custom speaking style instructions..."
                />
                {isCustomPrompt && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResetToPreset}
                  >
                    Reset to Preset
                  </Button>
                )}
              </div>
            )}
          </div>

        </div>

        {/* TTS Voice Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Volume2 className="h-5 w-5 text-gray-600" />
            <h2 className="text-xl font-semibold">Text-to-Speech Voice</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Choose a voice for your AI concierge. When enabled, the chatbot will speak responses aloud to visitors.
          </p>

          {/* Voice Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {TTS_VOICES.map((voice) => {
              const isActive = voice.id === currentTTSVoice;
              const isDisabled = voice.id === 'voice-none';
              return (
                <button
                  key={voice.id}
                  type="button"
                  onClick={() => handleTTSVoiceChange(voice.id)}
                  className={`
                    flex flex-col items-start gap-1 p-4 rounded-lg border-2 transition-all text-left
                    ${isActive
                      ? isDisabled
                        ? 'border-gray-400 bg-gray-100 ring-2 ring-gray-300'
                        : 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                    }
                  `}
                >
                  <div className="flex items-center gap-2 w-full">
                    {isDisabled ? (
                      <VolumeX className={`h-4 w-4 ${isActive ? 'text-gray-600' : 'text-gray-400'}`} />
                    ) : (
                      <Volume2 className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    )}
                    <span className={`text-sm font-medium ${isActive ? (isDisabled ? 'text-gray-700' : 'text-blue-700') : 'text-gray-700'}`}>
                      {voice.name}
                    </span>
                  </div>
                  {!isDisabled && (
                    <span className="text-xs text-gray-500 font-medium">{voice.nameJa}</span>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {!isDisabled && (
                      <>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          voice.gender === 'female' ? 'bg-pink-100 text-pink-700' :
                          voice.gender === 'male' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {voice.gender}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {voice.language}
                        </span>
                      </>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 line-clamp-2 mt-1">
                    {voice.description}
                  </span>
                  {isActive && (
                    <span className={`text-xs mt-1 ${isDisabled ? 'text-gray-600' : 'text-blue-600'}`}>
                      Selected
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* TTS Preview Section */}
          {currentTTSVoice !== 'voice-none' && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Preview Voice</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label htmlFor="ttsPreviewText" className="text-xs text-gray-500">Preview Text</Label>
                  <Input
                    id="ttsPreviewText"
                    value={ttsPreviewText}
                    onChange={(e) => setTtsPreviewText(e.target.value)}
                    placeholder="Enter text to preview..."
                    className="mt-1"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTTSPreview}
                  disabled={ttsPreviewPlaying || !ttsPreviewText.trim()}
                  className="flex items-center gap-2"
                >
                  {ttsPreviewPlaying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Playing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Preview
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Preview functionality coming soon. Voice selection will be saved with your venue settings.
              </p>
            </div>
          )}

        </div>

        {/* Error Banner */}
        {saveError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Failed to save venue</p>
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          </div>
        )}

        <form id="venue-edit-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Venue Title *</Label>
                <Input
                  id="title"
                  {...register('title')}
                  placeholder="e.g., My Restaurant"
                />
                {errors.title && (
                  <p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="vanitySlug">URL Slug</Label>
                <Input
                  id="vanitySlug"
                  {...register('vanitySlug')}
                  placeholder="e.g., my-restaurant (auto-generated if blank)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {currentVenue?.shortCode ? (
                    <>
                      Chatbot URL: <span className="font-mono">/talk/{currentVenue.shortCode}/{watch('vanitySlug') || currentVenue.vanitySlug || 'your-slug'}</span>
                    </>
                  ) : (
                    <>URL: /talk/{currentVenue?.id || venueId || 'venue-id'}/{watch('vanitySlug') || 'your-slug'}</>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  The slug is for SEO and can be changed anytime. Your chat URL remains stable.
                </p>
                {errors.vanitySlug && (
                  <p className="text-sm text-red-500 mt-1">{errors.vanitySlug.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  {...register('description')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell customers about your restaurant..."
                />
                {errors.description && (
                  <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Address</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="street">Street Address</Label>
                <Input id="street" {...register('address.street')} />
                {errors.address?.street && (
                  <p className="text-sm text-red-500 mt-1">{errors.address.street.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" {...register('address.city')} />
                  {errors.address?.city && (
                    <p className="text-sm text-red-500 mt-1">{errors.address.city.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="state">State/Province</Label>
                  <Input id="state" {...register('address.state')} />
                  {errors.address?.state && (
                    <p className="text-sm text-red-500 mt-1">{errors.address.state.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input id="postalCode" {...register('address.postalCode')} />
                  {errors.address?.postalCode && (
                    <p className="text-sm text-red-500 mt-1">{errors.address.postalCode.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" {...register('address.country')} />
                  {errors.address?.country && (
                    <p className="text-sm text-red-500 mt-1">{errors.address.country.message}</p>
                  )}
                </div>
              </div>

              {/* Geocoding Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Map Coordinates</h3>
                    <p className="text-xs text-gray-500">Look up coordinates from the address above</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGeocode}
                    disabled={geocodeLoading}
                    className="flex items-center gap-2"
                  >
                    {geocodeLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Looking up...
                      </>
                    ) : (
                      <>
                        <MapPin className="h-4 w-4" />
                        Lookup Coordinates
                      </>
                    )}
                  </Button>
                </div>

                {/* Geocode Error */}
                {geocodeError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{geocodeError.message}</p>
                  </div>
                )}

                {/* Geocode Success */}
                {geocodeSuccess && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-green-600">Coordinates found and updated!</p>
                  </div>
                )}

                {/* Lat/Long Display */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude" className="text-gray-500">Latitude</Label>
                    <Input
                      id="latitude"
                      value={watch('address.latitude')?.toFixed(6) || ''}
                      readOnly
                      placeholder="Not set"
                      className="bg-gray-50 text-gray-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude" className="text-gray-500">Longitude</Label>
                    <Input
                      id="longitude"
                      value={watch('address.longitude')?.toFixed(6) || ''}
                      readOnly
                      placeholder="Not set"
                      className="bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Powered by OpenStreetMap Nominatim. Coordinates are used for map display.
                </p>
              </div>

              {/* Timezone Section - inside Address card */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-medium text-gray-700">Restaurant Timezone</h3>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleTimezoneChange(getBrowserTimezone())}
                    className="text-xs"
                  >
                    Use My Timezone
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Used for accurate date/time in reservations and chat.
                </p>

                {/* Current timezone display */}
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    {currentTimezone} ({getTimezoneOffset(currentTimezone)})
                  </p>
                </div>

                {/* Common timezones grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                  {COMMON_TIMEZONES.map((tz) => {
                    const isActive = tz.value === currentTimezone;
                    const offset = getTimezoneOffset(tz.value);
                    return (
                      <button
                        key={tz.value}
                        type="button"
                        onClick={() => handleTimezoneChange(tz.value)}
                        className={`
                          px-2 py-1.5 rounded border text-left text-xs transition-all
                          ${isActive
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 text-gray-700'
                          }
                        `}
                      >
                        <span className="block">{tz.label}</span>
                        <span className={`block text-[10px] ${isActive ? 'text-blue-500' : 'text-gray-400'}`}>
                          {offset}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* All timezones dropdown */}
                <button
                  type="button"
                  onClick={() => setShowAllTimezones(!showAllTimezones)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  {showAllTimezones ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                  <span>All timezones</span>
                </button>

                {showAllTimezones && (
                  <div className="mt-2">
                    <select
                      value={currentTimezone}
                      onChange={(e) => handleTimezoneChange(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                    >
                      {getAvailableTimezones().map((tz) => (
                        <option key={tz} value={tz}>
                          {tz} ({getTimezoneOffset(tz)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" {...register('contact.phone')} placeholder="+1 (555) 123-4567" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" {...register('contact.email')} placeholder="info@restaurant.com" />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" {...register('contact.website')} placeholder="https://restaurant.com" />
              </div>
            </div>
          </div>

          {/* Hours of Operation */}
          <div className="bg-white rounded-lg shadow p-6">
            <HoursEditor hours={hours} onChange={setHours} />
          </div>

          {/* Photos */}
          <div className="bg-white rounded-lg shadow p-6">
            <PhotoUploader
              photos={photos}
              onChange={setPhotos}
              onUpload={handlePhotoUpload}
              authToken={session?.token}
              disabled={isNewVenue}
              disabledMessage="Save the venue first to upload photos"
            />
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow p-6">
            <DocumentUploader
              documents={documents}
              onChange={setDocuments}
              onUpload={handleDocumentUpload}
              disabled={isNewVenue}
              disabledMessage="Save the venue first to upload documents"
            />
          </div>

          {/* Website URLs for RAG */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="h-5 w-5 text-gray-600" />
              <h2 className="text-xl font-semibold">Website URLs</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Add website URLs that contain information about your restaurant. These pages will be scraped to help your AI concierge answer customer questions.
            </p>

            {/* Disclaimer checkbox */}
            <label className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={ragUrlDisclaimer}
                onChange={(e) => setRagUrlDisclaimer(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-amber-800">
                I confirm that I have the right to scrape these URLs and that they contain publicly accessible information about my restaurant.
              </span>
            </label>

            {/* Error message */}
            {ragUrlError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{ragUrlError}</p>
              </div>
            )}

            {/* Existing URLs */}
            {ragUrls.length > 0 && (
              <div className="mb-4 space-y-2">
                <Label className="text-gray-500">Added URLs</Label>
                {ragUrls.map((ragUrl) => (
                  <div
                    key={ragUrl.id}
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                  >
                    <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {ragUrl.title || ragUrl.url}
                      </p>
                      {ragUrl.title && (
                        <p className="text-xs text-gray-500 truncate">{ragUrl.url}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      ragUrl.status === 'active' ? 'bg-green-100 text-green-700' :
                      ragUrl.status === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {ragUrl.status}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveRagUrl(ragUrl.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* URL Input fields */}
            <div className="space-y-3">
              {ragUrlInputs.map((input, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="url"
                    value={input}
                    onChange={(e) => handleRagUrlInputChange(index, e.target.value)}
                    placeholder="https://your-restaurant.com/menu"
                    disabled={ragUrlValidating === index}
                    className={ragUrlErrorIndex === index ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <Button
                    type="button"
                    onClick={() => handleValidateAndAddUrl(index)}
                    disabled={ragUrlValidating === index || !ragUrlDisclaimer || !input.trim()}
                    className="flex-shrink-0"
                  >
                    {ragUrlValidating === index ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Add'
                    )}
                  </Button>
                  {ragUrlInputs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRagUrlInput(index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add another URL button */}
            <button
              type="button"
              onClick={handleAddRagUrlInput}
              className="mt-3 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add another URL
            </button>

            <p className="text-xs text-gray-400 mt-4">
              URLs are validated before being added. Only publicly accessible pages on allowed domains can be scraped.
            </p>
          </div>

          {/* SNS Links */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="h-5 w-5 text-gray-600" />
              <h2 className="text-xl font-semibold">Social Media Links</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Add your restaurant's social media accounts. These links can be displayed to customers and used by the AI concierge.
            </p>

            {/* Existing SNS links - with inline editing */}
            {snsLinks.length > 0 && (
              <div className="mb-4 space-y-3">
                <Label className="text-gray-500">Connected Accounts</Label>
                {snsLinks.map((link) => {
                  const platformInfo = getSNSPlatformById(link.platform);
                  const isEditing = editingSnsLinkId === link.id;
                  const needsUrl = !link.url;

                  return (
                    <div
                      key={link.id}
                      className={`flex items-center gap-3 p-3 rounded-lg ${isEditing || needsUrl ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isEditing || needsUrl ? 'bg-amber-200' : 'bg-gray-200'}`}>
                        <Share2 className={`h-4 w-4 ${isEditing || needsUrl ? 'text-amber-600' : 'text-gray-600'}`} />
                      </div>

                      {isEditing || needsUrl ? (
                        /* Inline edit mode */
                        <>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {platformInfo?.name || link.platform}
                            </p>
                            <Input
                              type="text"
                              value={link.url}
                              onChange={(e) => {
                                setSnsLinks(snsLinks.map(l =>
                                  l.id === link.id ? { ...l, url: e.target.value } : l
                                ));
                              }}
                              onBlur={() => {
                                // Exit edit mode when blurring, but only if URL is filled
                                if (link.url) {
                                  setEditingSnsLinkId(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && link.url) {
                                  setEditingSnsLinkId(null);
                                }
                              }}
                              placeholder={platformInfo?.placeholder || 'Enter URL'}
                              className="text-sm h-8"
                              autoFocus
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSnsLinks(snsLinks.filter(l => l.id !== link.id));
                              setEditingSnsLinkId(null);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-500"
                            title="Remove"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        /* Display mode for complete links */
                        <>
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => setEditingSnsLinkId(link.id)}
                            title="Click to edit"
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {link.label || platformInfo?.name || link.platform}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{link.url}</p>
                          </div>
                          <a
                            href={link.url.startsWith('@') ? '#' : link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-400 hover:text-blue-500"
                            title="Open link"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setSnsLinks(snsLinks.filter(l => l.id !== link.id))}
                            className="p-1.5 text-gray-400 hover:text-red-500"
                            title="Remove link"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add new SNS link */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Add New Link</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SNS_PLATFORMS.slice(0, 3).map((platform) => {
                  const alreadyAdded = snsLinks.some(l => l.platform === platform.id);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      disabled={alreadyAdded}
                      onClick={() => {
                        const newId = crypto.randomUUID();
                        const newLink: VenueSNSLink = {
                          id: newId,
                          platform: platform.id as SNSPlatform,
                          url: '',
                        };
                        setSnsLinks([...snsLinks, newLink]);
                        setEditingSnsLinkId(newId);
                      }}
                      className={`
                        flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left
                        ${alreadyAdded
                          ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                        }
                      `}
                    >
                      <Plus className={`h-4 w-4 ${alreadyAdded ? 'text-gray-300' : 'text-blue-500'}`} />
                      <span className="text-sm font-medium">{platform.name}</span>
                      {alreadyAdded && (
                        <span className="ml-auto text-xs text-green-600">Added</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* More platforms dropdown */}
              <details className="mt-3">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  More platforms...
                </summary>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {SNS_PLATFORMS.slice(3).map((platform) => {
                    const alreadyAdded = snsLinks.some(l => l.platform === platform.id);
                    return (
                      <button
                        key={platform.id}
                        type="button"
                        disabled={alreadyAdded}
                        onClick={() => {
                          const newId = crypto.randomUUID();
                          const newLink: VenueSNSLink = {
                            id: newId,
                            platform: platform.id as SNSPlatform,
                            url: '',
                          };
                          setSnsLinks([...snsLinks, newLink]);
                          setEditingSnsLinkId(newId);
                        }}
                        className={`
                          flex items-center gap-2 p-2 rounded border text-sm transition-all
                          ${alreadyAdded
                            ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                          }
                        `}
                      >
                        <Plus className={`h-3 w-3 ${alreadyAdded ? 'text-gray-300' : 'text-blue-500'}`} />
                        {platform.name}
                      </button>
                    );
                  })}
                </div>
              </details>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              Social media links help customers connect with your restaurant on their preferred platforms.
            </p>
          </div>

          {/* LINE Bot Integration */}
          {currentVenue?.shortCode && (
            <LineIntegrationSection shortCode={currentVenue.shortCode} venueId={currentVenue.id} />
          )}

          {/* Actions */}
          <div className="flex flex-col items-end gap-2 pt-4 border-t">
            {ragUrlErrorIndex !== null && (
              <p className="text-sm text-red-600">
                Please fix or clear the invalid URL before saving.
              </p>
            )}
            <p className="text-sm text-gray-500">
              Use the Save button in the header to save your changes.
            </p>
          </div>
        </form>

        {/* Debug: All Vault Assets */}
        <div className="bg-gray-800 rounded-lg shadow p-6 mt-8 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-yellow-500 text-xs font-mono px-2 py-0.5 bg-yellow-500/20 rounded">DEBUG</span>
              <h2 className="text-lg font-semibold text-white">All Vault Assets</h2>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fetchDebugAssets}
                disabled={debugAssetsLoading}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                {debugAssetsLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Fetch All Assets'
                )}
              </Button>
              {debugAssets.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDebugAssets(!showDebugAssets)}
                  className="text-gray-300 hover:bg-gray-700"
                >
                  {showDebugAssets ? (
                    <><ChevronUp className="h-4 w-4 mr-1" /> Hide</>
                  ) : (
                    <><ChevronDown className="h-4 w-4 mr-1" /> Show ({debugAssets.length})</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {debugAssetsError && (
            <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg mb-4">
              <p className="text-sm text-red-400">{debugAssetsError}</p>
            </div>
          )}

          {showDebugAssets && debugAssets.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {debugAssets.map((asset: unknown, index: number) => {
                const a = asset as Record<string, unknown>;
                return (
                  <div key={a.uuid as string || index} className="p-3 bg-gray-900 rounded-lg border border-gray-700">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{a.name as string}</p>
                        <p className="text-xs text-gray-400 font-mono">{a.uuid as string}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs px-1.5 py-0.5 bg-blue-900/50 text-blue-300 rounded">{String(a.type)}</span>
                          <span className="text-xs px-1.5 py-0.5 bg-green-900/50 text-green-300 rounded">{String(a.status)}</span>
                          <span className="text-xs text-gray-500">v{String(a.version)}</span>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <p>{new Date(a.createdAt as string).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {'data' in a && a.data !== null && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">View data</summary>
                        <pre className="mt-2 p-2 bg-black rounded text-xs text-gray-300 overflow-x-auto max-h-48">
                          {JSON.stringify(a.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!debugAssetsLoading && debugAssets.length === 0 && (
            <p className="text-sm text-gray-500">Click "Fetch All Assets" to load assets from vault.</p>
          )}
        </div>

        {/* Status Change Confirmation Dialog */}
        <StatusChangeDialog
          isOpen={statusDialog.isOpen}
          currentStatus={currentStatus}
          newStatus={statusDialog.newStatus}
          venueName={watch('title') || ''}
          onConfirm={handleStatusChangeConfirm}
          onCancel={handleStatusChangeCancel}
          isLoading={statusChangeLoading}
        />

        {/* Footer with version */}
        <footer className="mt-8 pt-4 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">v{import.meta.env.VITE_APP_VERSION}</p>
        </footer>

      </div>
    </div>
  );
};
